<template>

  <ion-page id="voicemenu-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />

      </div>

      <div v-show="showState == SHOW_MENU">
        <TopLine :style="styleTopline" :title="topLineTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="voice-menu-list" :style="styleMiddle">
       
          <ion-item>
            <ion-label @click="onClick('/selectlanguage')">
              {{ translateEnglishWords("Language") }}
              <div class="indent">{{ languageString }}</div>
            </ion-label>
          </ion-item>

          <ion-item>
            <ion-label @click="onClick('/selectvoices')">
              {{ translateEnglishWords("Speaking Voices") }}
              <div class="indent">{{ translateEnglishWords("Player") }} : {{ playerVoiceName }} - {{ playerVoiceLang }}</div>
              <div class="indent">{{ translateEnglishWords("Opponent") }} : {{ opponentVoiceName }} - {{ opponentVoiceLang }}</div>
            </ion-label>
          </ion-item>

          <ion-item>
            <ion-label @click="onClick('/recognizerdemo')">
              {{ translateEnglishWords("Speech Recognizer Practice") }}
              <div class="indent">{{ translatePhrase("Learn what the speech recognizer hears") }}</div>
            </ion-label>
          </ion-item>

        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';

  import { computed } from "@vue/reactivity"
  import { ref, watch } from 'vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { useGetData, generateErrorAlertText } from '../../composables/getData.js'
  import { parseWordsSaid } from '@/composables/wordlistSupport.js'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation';
  import { verifyMp3NameLanguage, getMp3Voices , findMp3Entry } from '@/composables/mp3Support.js'

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import ExplainTour from '@/components/ExplainTour.vue'

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  console.log("*** IN VOICE MENU")
  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()  
  const windowSizeStore = useWindowSizeStore()

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const useMp3Voices = connectionStore.getUseMp3Voices
  const isDark = ref(connectionStore.getDarkmode != "light")
  var isSubscriber = connectionStore.getIsSubscriber

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const topLineTitle = ref(translateEnglishWords("Language & Voices Menu"))

  const wordlistMenu = "command scroll up down done explain ignore voice voices speaking language languages speech recognizer mute listen";
  
  var wordlist = wordlistMenu
  emit("word-list",wordlist)

  const SHOW_MENU = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_MENU)

  const TOOLBAR_EXPLAIN = 0

  const toolbarElements = [
    { index : TOOLBAR_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ]

  const toolbarTrigger = ref(0)

  const languageInUse = connectionStore.getLanguageInUse
  const languageString = ref(translateEnglishWords("System"))
  switch (languageInUse) {
    case "en" :
      languageString.value = translateEnglishWords("English")
      break
    case "es" :
      languageString.value = translateEnglishWords("Spanish")
      break
    case "it" :
      languageString.value = translateEnglishWords("Italian")
      break
    case "fr" :
      languageString.value = translateEnglishWords("French")
      break
    case "de" :
      languageString.value = translateEnglishWords("German")
      break
    case "pt" :
      languageString.value = translateEnglishWords("Portuguese")
      break
  }

  const playerVoiceName = ref((useMp3Voices) ? connectionStore.getPlayerMp3Name : connectionStore.getPlayerVoiceName)
  const playerVoiceLang = ref((useMp3Voices) ? connectionStore.getPlayerMp3Lang : connectionStore.getPlayerVoiceLang)
  const playerVoiceVendor = ref((useMp3Voices) ? connectionStore.getPlayerMp3Vendor : "")
  const opponentVoiceName = ref((useMp3Voices) ? connectionStore.getOpponentMp3Name : connectionStore.getOpponentVoiceName)
  const opponentVoiceLang = ref((useMp3Voices) ? connectionStore.getOpponentMp3Lang : connectionStore.getOpponentVoiceLang)
  const opponentVoiceVendor = ref((useMp3Voices) ? connectionStore.getOpponentMp3Vendor : "")

  if (useMp3Voices) {
    const playerEntry = findMp3Entry(playerVoiceVendor.value,playerVoiceLang.value,playerVoiceName.value)
    const opponentEntry = findMp3Entry(opponentVoiceVendor.value,opponentVoiceLang.value,opponentVoiceName.value)
    console.log("ENTRYS ",playerEntry,opponentEntry)
    if (playerEntry != null) {
      playerVoiceName.value = playerEntry.displayName
      playerVoiceLang.value = ""
    }
    if (opponentEntry != null) {
      opponentVoiceName.value = opponentEntry.displayName
      opponentVoiceLang.value = ""
    }
  }

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  showState.value = SHOW_MENU

  doStartupCheck(false,afterStartupCallbackRoutine)

  watch(wordsSaidTrigger, (newVal,oldVal) => {
     console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  function afterStartupCallbackRoutine(stuffChanged) {
  }

  function toplineClick(whatClicked) {
    console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "voices_menu" : ("ERROR:" + errorText);
    wordlist = connectionStore.getExplainTourWords
    emit("word-list",wordlist)
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_MENU
        wordlist = wordlistMenu
        emit("word-list",wordlist)
        break
    }
  }

  // END Explain Tour Routines

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break
    }
  }

  function onClick(where) {
    emit("where-next",where)
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)
    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    if (showState.value == SHOW_EXPLAINTOUR) {
      //console.log("*** words for explain tour",text)
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }
    switch (returnArray[0]) {
      case "scroll" :
        scrollElement("voice-menu-list",returnArray[1])
        break
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize(null)
        break;
      case "recognizer" :
        emit("where-next","/recognizerdemo")
        break;
      case "done" :
        emit("where-next","/accountmenu")
        break;
      case "language" :
        emit("where-next","/selectlanguage")
        break
      case "voices" :
        emit("where-next","/selectvoices")
        break
    }
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain" || currentWord == "mute" || currentWord == "listen") {
            return [currentWord,""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
  
        if (currentWord == "language" || currentWord == "languages") {
            return ["language",""]
        }
        if (currentWord == "speech" || currentWord == "recognizer")
          return ["recognizer",""]
        if (currentWord == "voice" || currentWord == "voices" || currentWord == "speaking") {
            return ["voices",""]
        }
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

</script>

<style scoped>

ion-label {
    cursor: pointer;
    margin: 0px;
    padding : 0px;
 }

 ion-item.text-message {
    margin-top: 0px; 

    cursor: pointer;
    padding : 0px;
 }


 .indent {
  margin-left : 40px;
  font-style: italic;
 }

ion-item {
  margin-bottom : 20px;
}
</style>